import type { DoctorPayment } from '../../services/httpServices/diagnosticService';

/**
 * Shared vocabulary for the Finance → Payouts workspace.
 *
 * The whole payout domain is **payee-agnostic** (ADR-015): a doctor and a
 * referral partner are the same ledger row, told apart by `payeeType`. Keeping
 * the formatting helpers in one plain module (no JSX) means the Outstanding /
 * Vouchers / Trash tabs can never drift apart.
 */

export const CURRENCY = '৳';
export const PAYOUT_PAGE_SIZE = 20;

export type PayeeKind = 'DOCTOR' | 'REFERRAL';

/** Settlement status of one payee's aggregated accruals. */
export type PayoutStatusKind = 'PENDING' | 'POSTED' | 'PARTIAL' | 'SETTLED';

export interface PayoutStatus {
  kind: PayoutStatusKind;
  label: string;
  /** Breakdown shown on the badge tooltip, so the label never hides detail. */
  hint: string;
}

export function money(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Settlement status of a payee row on the Outstanding ledger.
 *
 * The ledger rolls a payee's distributions into three buckets — `pendingShare`,
 * `postedShare` and `settledShare` — so the status is derived from the mix:
 *   • Settled           — nothing is outstanding any more
 *   • Partially settled — some shares paid, some still owed
 *   • Pending           — owed but not yet posted (accrual only)
 *   • Posted            — owed and posted, i.e. ready to pay
 * `postedShare > 0` wins over `pendingShare` because the money is already
 * payable; the pending portion is still surfaced in the tooltip `hint`.
 */
export function payoutStatusOf(row: {
  pendingShare: number;
  postedShare: number;
  settledShare: number;
  outstandingShare: number;
}): PayoutStatus {
  if (row.outstandingShare <= 0) {
    return {
      kind: 'SETTLED',
      label: 'Settled',
      hint: 'All accrued shares have been paid.',
    };
  }

  if (row.settledShare > 0) {
    return {
      kind: 'PARTIAL',
      label: 'Partially settled',
      hint: `Settled ${money(row.settledShare)} · outstanding ${money(row.outstandingShare)}`,
    };
  }

  if (row.postedShare > 0) {
    return {
      kind: 'POSTED',
      label: 'Posted',
      hint:
        `Posted and payable ${money(row.postedShare)}` +
        (row.pendingShare > 0 ? ` · pending ${money(row.pendingShare)}` : ''),
    };
  }

  return {
    kind: 'PENDING',
    label: 'Pending',
    hint: `Accrued but not yet posted ${money(row.pendingShare)}`,
  };
}

export function doctorName(
  doctor?: { name?: string; firstName?: string; lastName?: string } | null,
): string {
  if (!doctor) return '—';
  return doctor.name || `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || '—';
}

/** True when a voucher belongs to a referral partner rather than a doctor. */
export function isReferralVoucher(p: DoctorPayment): boolean {
  return p.payeeType === 'REFERRAL' || !!p.referralId || !!p.referral;
}

/** Resolve a voucher's payee name regardless of payee kind. */
export function voucherPayeeName(p: DoctorPayment): string {
  return (
    p.payeeNameSnapshot ||
    p.referral?.name ||
    (p.doctor ? doctorName(p.doctor) : '') ||
    '—'
  );
}
